import Experience from "../Experience.js";
import { logTriangleDefinitions } from "three-bvh-csg";


/**
 * Manages and updates the sizes and dimensions of the canvas and related elements.
 * Handles window resize events and communicates updates through an event hub.
 * @category Experience
 * @subcategory Utils
 */
export default class Sizes {
  constructor() {
    /**
     * Reference to the main Experience instance.
     * @type {Experience}
     */
    this.experience = new Experience();

    /**
     * Canvas DOM element managed by the Experience.
     * @type {HTMLCanvasElement}
     */
    this.canvas = this.experience.canvas;

    /**
     * Event hub for emitting and listening to application-wide events.
     * @type {EventEmitter}
     */
    this.eventHub = this.experience.eventHub;

    /**
     * Camera instance managed by the Experience.
     * @type {Object}
     */
    this.camera = this.experience.camera;

    /**
     * Renderer instance managed by the Experience.
     * @type {Object}
     */
    this.renderer = this.experience.renderer;

    /**
     * Device pixel ratio, capped at 2 for performance.
     * @type {number}
     */
    this.pixelRatio = Math.min(window.devicePixelRatio, 2);

    /**
     * Canvas element specifically targeted by the application.
     * @type {HTMLCanvasElement}
     */
    this.canvasElement = document.querySelector("canvas.webgl");

    /**
     * Object to store current canvas dimensions and pixel ratio.
     * @type {Object}
     */
    this.reSize = {
      width: this.canvasElement.getBoundingClientRect().width,
      height: this.canvasElement.getBoundingClientRect().height,
      pixelRatio: this.pixelRatio,
    };

    // Listen for window resize events
    window.addEventListener("resize", () => {
      this.eventHub.emit("globalResize");
    });

    // Listen for custom resize events emitted by the event hub
    this.eventHub.on("globalResize", () => {
      this.updateSizes();
    });
  }

  cleanup() {
    window.removeEventListener("resize", this.onResize);
    this.eventHub.off("globalResize", this.updateSizes);
    this.canvasElement = null;
    this.reSize = null;
}

  /**
   * Updates canvas dimensions and recalculates pixel ratio.
   * Emits a `resize` event with the updated dimensions.
   */
  updateSizes() {
    // Force reflow
    this.canvasElement.style.width = "100%";
    this.canvasElement.style.height = "100%";

    // Get updated dimensions
    const newWidth = this.canvasElement.getBoundingClientRect().width;
    const newHeight = this.canvasElement.getBoundingClientRect().height;

    this.reSize.width = newWidth;
    this.reSize.height = newHeight;
    this.reSize.pixelRatio = Math.min(window.devicePixelRatio, 2);

    // Emit the updated size data through the event hub
    this.eventHub.emit("resize", this.reSize);
  }
}
